import { useCallback, useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import {
  AllDoneFill,
  ArrowBackOutline,
  ArrowRightFill,
  EditOutline,
  GlobeFill,
  ImageOutline,
  PersonDirections,
  RockUphill,
  StarOnPlatform,
} from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Button, Loader, Stack, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import usePageLayout from '@/components/PageLayout/usePageLayout';
import { TypographyWithBulletPoint } from '@/components/TypographyWithBulletPoint';
import useNavigation from '@/hooks/useNavigation';
import { ContentsFragment } from '@/modules/corporate/modules/admin/graphql/fragments/generated/Contents';
import { horizontalDivider } from '@/modules/corporate/modules/home/views/Home/components/HomepageCalendar';
import { useQuestionsQuery } from '@/modules/corporate/modules/questions/views/AddQuestionModal/graphql/queries/generated/questions';
import TemplatesHeaderNode from '@/modules/templates/views/components/TemplatesHeaderNode';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { UserSurveyResponseVersionStatusEnum } from '@/types/schema';

import PersonaCardGrid from '../../KnowledgeAssetManager/components/PersonaCardGrid';
import SolutionCardGrid from '../../KnowledgeAssetManager/components/SolutionCardGrid';
import { useKnowledgeAssetsQuery } from '../../KnowledgeAssetManager/graphql/query/generated/knowledgeAssets';
import EmptyState from '../components/EmptyState';
import ImageViewerModal from '../components/ImageViewerModal';
import { usePersonaQuery } from '../graphql/queries/generated/persona';
import { usePersonaDetailsQuery } from '../graphql/queries/generated/personaDetails';
import { usePersonaQuestionIdsQuery } from '../graphql/queries/generated/personaQuestionIds';
import { DetailsPageProps } from '../types';

const parsePipeSeparatedString = (text: string): string[] => {
  if (!text) return [];
  return text
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean);
};

const stripHtmlTags = (text: string): string => {
  if (!text) return '';

  // Handle multiple links separated by '|'
  if (text.includes('|')) {
    return text
      .split('|')
      .map((part) => {
        const linkMatch = part.match(/<a[^>]*href=["']([^"']*)["'][^>]*>/);
        return linkMatch ? linkMatch[1] : part.replace(/<\/?[^>]+(>|$)/g, '').trim();
      })
      .filter(Boolean)
      .join('|');
  }

  // Handle single link
  const linkMatch = text.match(/<a[^>]*href=["']([^"']*)["'][^>]*>/);
  if (linkMatch) {
    return linkMatch[1];
  }

  return text.replace(/<\/?[^>]+(>|$)/g, '').trim();
};

const getResponseByQuestionText = (
  questionText: string,
  questions: any,
  responses: any,
  getIds?: boolean,
): string => {
  // Find the question with matching text
  const question = questions?.questions?.edges?.find(
    (edge: any) => edge?.node?.questionText == questionText,
  )?.node;

  if (!question) return '';

  // Get all responses from all edges
  const allResponses = responses?.userSurveyResponses?.edges?.map((edge: any) => edge?.node) || [];

  // Find the response for this question
  const response = allResponses.find((response: any) => response?.entityId === question?.id);

  if (getIds) {
    const ids =
      response?.surveyResponse?.optionResponse?.map((option: any) => option?.optionId) || [];
    return ids.join(', ');
  }

  const textResponse =
    response?.surveyResponse?.textResponse ||
    response?.surveyResponse?.optionResponse?.map((option: any) => option?.optionText).join(', ') ||
    response?.surveyResponse?.attachments?.map((attachment: any) => attachment?.url).join('|') ||
    '';
  return stripHtmlTags(textResponse);
};

const getImageResponseByQuestionText = (
  questionText: string,
  questions: any,
  responses: any,
): string[] | null => {
  // Find the question with matching text
  const question = questions?.questions?.edges?.find(
    (edge: any) => edge?.node?.questionText == questionText,
  )?.node;

  if (!question) return null;

  // Get all responses from all edges
  const allResponses = responses?.userSurveyResponses?.edges?.map((edge: any) => edge?.node) || [];

  // Find the response for this question
  const response = allResponses.find((response: any) => response?.entityId === question?.id);

  return response?.surveyResponse?.attachments?.map((attachment: any) => attachment?.url) || null;
};

interface CardData {
  title: string;
  responses: string[];
}

const getIconForCard = (title: string) => {
  switch (title) {
    case 'Key Business Challenges':
      return <RockUphill fillColorToken="icon-active" height={24} width={24} />;
    case 'What success looks like to them?':
      return <StarOnPlatform fillColorToken="icon-active" height={24} width={24} />;
    case 'What influences their purchase?':
      return <PersonDirections fillColorToken="icon-active" height={24} width={24} />;
    default:
      return <AllDoneFill fillColorToken="icon-active" height={24} width={24} />;
  }
};

const PersonaJourneyDetails = ({ assetId, isPreview }: DetailsPageProps) => {
  const { isMobile } = useScreen();
  const [currentTabId, setCurrentTabId] = useState<string>('insights');
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});
  const isAdminRoute = useAdminRoute();
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);

  const [{ data: personaData, fetching: fetchingPersonaData }] = usePersonaQuery({
    variables: {
      filters: {
        id: assetId,
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const attemptIds =
    (personaData?.content as any)?.userSurveyAttemptConfig?.map(
      (attempt: any) => attempt?.attemptId,
    ) || [];

  const userSurveyIds = (personaData?.content as any)?.userSurveyAttemptConfig?.map(
    (attempt: any) => attempt?.userSurveyId,
  );

  const [{ data: personaDetailsData, fetching: fetchingPersonaDetailsData }] =
    usePersonaDetailsQuery({
      variables: {
        filters: {
          attemptIds,
          ...(isPreview ? { versionStatus: UserSurveyResponseVersionStatusEnum.Draft } : {}),
        },
        first: -1,
      },
      pause: !attemptIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const [{ data: personaQuestionsData, fetching: fetchingUserSurveyDetailsData }] =
    usePersonaQuestionIdsQuery({
      variables: {
        filters: {
          ids: userSurveyIds,
        },
      },
      pause: !userSurveyIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const questionIds =
    personaQuestionsData?.userSurveys?.edges?.flatMap(
      (edge) =>
        edge?.node?.questionConfig?.surveyQuestions?.map(
          (question) => question?.questionId || '',
        ) || [],
    ) || [];

  const [{ data: questionsData, fetching: fetchingQuestionsData }] = useQuestionsQuery({
    variables: {
      filters: {
        ids: questionIds,
      },
    },
    pause: !questionIds?.length,
    requestPolicy: 'cache-and-network',
  });

  const title = getResponseByQuestionText('Journey Name', questionsData, personaDetailsData);
  const rawPersonaIds = getResponseByQuestionText(
    'Persona',
    questionsData,
    personaDetailsData,
    true,
  );
  const personaIds = (rawPersonaIds as string).split(',').map((id) => id.trim());
  const description = getResponseByQuestionText('Description', questionsData, personaDetailsData);
  const imageResponse = getResponseByQuestionText(
    'Upload Image',
    questionsData,
    personaDetailsData,
  );
  const images = imageResponse?.split('|').filter(Boolean) || [];

  const rawSolutionIds = getResponseByQuestionText(
    'Solutions',
    questionsData,
    personaDetailsData,
    true,
  );
  const solutionIds = (rawSolutionIds as string).split(',').map((id) => id.trim());

  const links = parsePipeSeparatedString(
    getResponseByQuestionText('Link to journey document', questionsData, personaDetailsData),
  );

  const [{ data: personasData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: personaIds || [],
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const [{ data: solutionsData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: solutionIds || [],
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const insightsQuestions = [
    'Key Business Challenges',
    'What success looks like to them?',
    'What influences their purchase?',
  ];

  const isLoading =
    fetchingPersonaData ||
    fetchingPersonaDetailsData ||
    fetchingUserSurveyDetailsData ||
    fetchingQuestionsData;

  const pageTitle = (personaData?.content as any)?.name;

  const navigation = useNavigation();
  const handleEditClick = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/persona-journey/${assetId}/edit`);
  }, [assetId, navigation]);

  const onBack = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/persona-journey`);
  }, [assetId, navigation]);

  usePageLayout({
    title: pageTitle,
    LeftNode: <TemplatesHeaderNode isPreview={isPreview} title={pageTitle} onBack={onBack} />,
    RightNode:
      isPreview || isMobile ? null : (
        <Button StartIcon={EditOutline} variant="outlined" onClick={handleEditClick}>
          Edit Solution Journey
        </Button>
      ),
    showHeader: !isMobile,
  });

  const toggleCard = (index: number) => {
    setExpandedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const getCardData = (): CardData[] => {
    const questions = insightsQuestions;

    return questions.map((questionText) => ({
      title: questionText,
      responses: parsePipeSeparatedString(
        getResponseByQuestionText(questionText, questionsData, personaDetailsData) as string,
      ),
    }));
  };

  const onbackPress = useCallback(() => {
    //navigation.replace(routes.LibraryPortal(), { appendQuery: false });
    navigation.back();
  }, [navigation]);
  if (isLoading) {
    return (
      <Stack alignItems="center" className="h-[calc(100vh-64px)] w-full" justifyContent="center">
        <Loader size={40} />
      </Stack>
    );
  }

  return (
    <Stack
      alignItems="center"
      className="mt-48 mb-128 max-w-[900px] min-w-[700px] mx-auto xs:min-w-full xs:p-12 xs:mt-12"
      direction="col">
      {isAdminRoute ? null : (
        <Stack className="w-full mb-12">
          <ArrowBackOutline className="h-24 w-24 cursor-pointer" onClick={onbackPress} />
        </Stack>
      )}
      <Stack alignItems="start" className="w-full" direction="col">
        <Stack className="" direction="col" gapY={12}>
          <Typography variant="heading-m">{title}</Typography>
          <Typography className="text-text-secondary" variant="body-short-02">
            {description}
          </Typography>
        </Stack>
        <div className={cn(horizontalDivider, 'my-32 w-full bg-interactive-secondary-hover')} />
      </Stack>
      {(personasData?.contents?.edges?.length ?? 0) > 0 && (
        <Stack className="self-start xs:w-full" direction="col" gapY={24}>
          <Typography className="pl-4" variant="display-s">
            Persona
          </Typography>
          <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1 xs:w-full">
            {personasData?.contents?.edges?.map((edge, index) => (
              <PersonaCardGrid
                key={index}
                hideActionMenu={true}
                index={index}
                persona={edge?.node as ContentsFragment}
              />
            ))}
          </Stack>
        </Stack>
      )}
      <Stack className="self-start pt-32 w-full" direction="col" gapY={24}>
        <Typography className="pl-4" variant="display-s">
          Images
        </Typography>
        <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1">
          <Stack
            className="border border-border rounded-12 h-[270px] w-full p-20 pb-24"
            direction="col">
            <Stack justifyContent="between">
              <Stack alignItems="center" gapX={8}>
                <ImageOutline fillColorToken="icon-active" height={18} width={18} />
                <Typography variant="heading-xs">Images</Typography>
              </Stack>
              {(images?.length ?? 0) > 0 && (
                <Typography
                  className="cursor-pointer text-text-link hover:underline"
                  variant="body-long-01"
                  onClick={() => setIsImageViewerOpen(true)}>
                  View All
                </Typography>
              )}
            </Stack>
            <Stack alignItems="center" className="flex-1 mt-10" justifyContent="center">
              {(images?.length ?? 0) > 0 ? (
                <div className="w-full h-full relative">
                  <Image
                    alt="Journey Image"
                    className="absolute inset-0 w-full h-full object-cover rounded-8"
                    height={200}
                    src={images[0]}
                    width={400}
                  />
                </div>
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      {(solutionsData?.contents?.edges?.length ?? 0) > 0 && (
        <Stack className="self-start pt-32 xs:w-full" direction="col" gapY={24}>
          <Typography className="pl-4" variant="display-s">
            Solutions
          </Typography>
          <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1 xs:w-full !max-w-fit">
            {solutionsData?.contents?.edges?.map((edge, index) => (
              <SolutionCardGrid
                key={index}
                index={index}
                solution={edge?.node as ContentsFragment}
              />
            ))}
          </Stack>
        </Stack>
      )}
      <Stack className="self-start pt-32 xs:w-full" direction="col" gapY={24}>
        <Typography className="pl-4" variant="display-s">
          Insights
        </Typography>
        <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1 xs:w-full">
          {getCardData().map((card, index) => {
            const isEmpty = !card.responses || card.responses.length === 0;

            return (
              <Stack
                key={index}
                className={`border border-border rounded-12 p-20 ${
                  expandedCards[index] && !isEmpty ? '' : 'max-h-[224px]'
                } transition-all duration-300 relative`}
                direction="col"
                gapY={12}>
                <Stack alignItems="center" gapX={8}>
                  <AllDoneFill fillColorToken="icon-active" height={24} width={24} />
                  <Typography variant="heading-xs">{card.title}</Typography>
                </Stack>
                <Stack className="overflow-hidden relative" direction="col" gapY={8}>
                  {isEmpty ? (
                    <EmptyState />
                  ) : (
                    <>
                      {card.responses.map((response: string, responseIndex: number) => (
                        <TypographyWithBulletPoint
                          key={responseIndex}
                          className="text-text-secondary"
                          text={response}
                          variant="body-long-01"
                        />
                      ))}
                      {!expandedCards[index] && (
                        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
                      )}
                    </>
                  )}
                </Stack>
                {!isEmpty && card.responses.length > 2 && (
                  <Stack
                    className="self-start mt-4 cursor-pointer"
                    onClick={() => toggleCard(index)}>
                    <Stack alignItems="center" gapX={4}>
                      <Typography variant="heading-xxs">
                        {expandedCards[index] ? 'See less' : 'See more'}
                      </Typography>
                      <ArrowRightFill
                        className={`transform transition-transform duration-300 ${
                          expandedCards[index] ? '-rotate-90' : ''
                        }`}
                        height={16}
                        width={16}
                      />
                    </Stack>
                  </Stack>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
      <Stack className="self-start pt-32 w-full" direction="col" gapY={24}>
        <Typography className="pl-4" variant="display-s">
          Documentations
        </Typography>
        <Stack className="grid grid-cols-2 gap-16 xs:grid-cols-1 xs:w-full">
          <Stack
            className="border border-border rounded-12 h-[270px] w-full p-20 pb-24"
            direction="col">
            <Stack justifyContent="between">
              <Stack alignItems="center" gapX={8}>
                <GlobeFill fillColorToken="icon-active" height={18} width={18} />
                <Typography variant="heading-xs">Links</Typography>
              </Stack>
            </Stack>
            <Stack className="flex-1 mt-24 overflow-hidden" direction="col">
              {(links?.length ?? 0) > 0 ? (
                links.map((link, index) => (
                  <Stack
                    key={index}
                    className="cursor-pointer group border-b border-border pb-12 mb-12 w-full"
                    onClick={() => window.open(link, '_blank')}>
                    <Typography
                      key={index}
                      className="text-link-external group-hover:text-link-external-hover"
                      variant="body-long-01">
                      {link}
                    </Typography>
                  </Stack>
                ))
              ) : (
                <EmptyState />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <ImageViewerModal
        images={images}
        isOpen={isImageViewerOpen}
        onClose={() => setIsImageViewerOpen(false)}
      />
    </Stack>
  );
};

export default PersonaJourneyDetails;
